import React, { useCallback } from 'react';
import {
  authState,
  companyState,
  SidebarItemsModel,
  Subjects,
} from '@jaam/web-core';
import { useLocalNavigation } from '../../../context/NavigationProvider';
import { SidebarItem } from './sidebaritem';
import clsx from 'clsx';
import {
  isAccountant,
  isAdmin,
  hasRoleData,
} from '@jaam/web-core';
import { useRecoilValue } from 'recoil';
import { PermissionObject } from '@jaam/commons/constants';
import {
  PAYROLLR_SIDEBAR_ITEMS_HIDDEN_FOR_ADMIN,
  PAYROLLR_SIDEBAR_ITEMS_HIDDEN_FOR_STAFF,
} from '../../../constants/payrollSidebarItems';

interface ISidebarProps {
  SidebarItems: SidebarItemsModel[];
}

// Items that should be hidden for admin users

const Sidebar = ({ SidebarItems }: ISidebarProps): React.ReactElement => {
  const { navBarItems, showSideMenu, verifyAbility } = useLocalNavigation();

  const { user } = useRecoilValue(authState);
  const company = useRecoilValue(companyState).activeCompany;

  // Loading state can be handled with a skeleton UI
  const renderLoadingState = useCallback(
    () => (
      <aside
        className={clsx(
          showSideMenu && 'hidden',
          !showSideMenu && [
            'max-sm:bg-white max-sm:[box-shadow:0px_-3px_16px_0px_#0000000F] sm:h-full fixed gap-4 left-0 bottom-0 right-0 flex justify-around items-center z-20 h-[64px]',
            'sm:static sm:items-center sm:justify-start sm:flex-col xl:w-[204px] sm:bg-transparent xl:mr-0',
          ],
        )}
        aria-label="Sidebar"
      >
        {Array.from({ length: 7 }).map((_, index) => (
          <div key={index} className="flex items-center gap-2 animate-pulse">
            <div className="w-8 h-8 bg-white rounded-full"></div>
            <div className="w-32 h-6 bg-white rounded"></div>
          </div>
        ))}
      </aside>
    ),
    [showSideMenu],
  );

  const renderSidebarItem = useCallback(
    (item: SidebarItemsModel, index: number) => {
      const isLastItem = index === navBarItems.length - 1;
      const canAccess =
        verifyAbility.can('read', item.name as Subjects) ||
        verifyAbility.can('read', 'all');

      // Check if the item should be hidden for admin users
      const shouldHideForAdmin =
        isAdmin(company || undefined) &&
        PAYROLLR_SIDEBAR_ITEMS_HIDDEN_FOR_ADMIN.includes(item.name);

      // Only hide Staff menu for non-admin/non-accountant when we have role data for this company.
      // When switching company, roles may not be loaded yet (userCompanies empty), so we show the item.
      const hasRoleDataForCompany = hasRoleData(company || undefined);
      const shouldHideForStaff =
        hasRoleDataForCompany &&
        !isAdmin(company || undefined) &&
        !isAccountant(company || undefined) &&
        PAYROLLR_SIDEBAR_ITEMS_HIDDEN_FOR_STAFF.includes(item.name);

      // Hide Admin item when user does not have global isAdmin
      const shouldHideAdminItem =
        item.name === PermissionObject.ADMIN && !user?.isAdmin;

      if (
        !canAccess ||
        shouldHideForAdmin ||
        shouldHideForStaff ||
        shouldHideAdminItem
      )
        return null;

      return (
        <SidebarItem
          item={item}
          className={clsx(
            'w-full',
            isLastItem ? 'lg:flex hidden' : item.hideOnMobile && 'lg:flex hidden',
            item.showOnlyOnMobile && 'sm:!hidden !flex',
          )}
          key={`${item.path}-${index}`}
        />
      );
    },
    [navBarItems.length, verifyAbility, company, user],
  );

  // If verifyAbility is not available yet, show loading state
  if (!verifyAbility) {
    return renderLoadingState();
  }

  return (
    <aside
      className={clsx(
        showSideMenu && 'hidden',
        !showSideMenu && [
          'max-sm:bg-white max-sm:[box-shadow:0px_-3px_16px_0px_#0000000F] sm:h-full fixed left-0 bottom-0 right-0 flex justify-around items-center z-20 max-sm:overflow-x-auto',
          'sm:static sm:items-center sm:justify-start sm:flex-col xl:w-[204px] sm:bg-transparent xl:mr-0',
        ],
      )}
      aria-label="Sidebar"
    >
      {(navBarItems as SidebarItemsModel[])?.map((item, index) =>
        renderSidebarItem(item, index),
      )}
    </aside>
  );
};

export default React.memo(Sidebar);
